import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeHandCardsWidth, sortDoudizhuCards, translateCardData } from '../utils';

const TARGETS = ['0', '1', '2', 'landlord'];
const TARGET_LIMITS = {
    '0': 17,
    '1': 17,
    '2': 17,
    landlord: 3,
};

const DECK_ROWS = [
    ['RJ', 'BJ'],
    ['S2', 'C2', 'H2', 'D2'],
    ['SA', 'CA', 'HA', 'DA'],
    ['SK', 'CK', 'HK', 'DK'],
    ['SQ', 'CQ', 'HQ', 'DQ'],
    ['SJ', 'CJ', 'HJ', 'DJ'],
    ['ST', 'CT', 'HT', 'DT'],
    ['S9', 'C9', 'H9', 'D9'],
    ['S8', 'C8', 'H8', 'D8'],
    ['S7', 'C7', 'H7', 'D7'],
    ['S6', 'C6', 'H6', 'D6'],
    ['S5', 'C5', 'H5', 'D5'],
    ['S4', 'C4', 'H4', 'D4'],
    ['S3', 'C3', 'H3', 'D3'],
];

function ManualDealDialog({ open, onClose, onConfirm }) {
    const { t } = useTranslation();
    const [activeTarget, setActiveTarget] = useState('0');
    const [assignments, setAssignments] = useState({});

    useEffect(() => {
        if (open) {
            setActiveTarget('0');
            setAssignments({});
        }
    }, [open]);

    const getTargetCards = (target) =>
        sortDoudizhuCards(
            Object.keys(assignments).filter((card) => assignments[card] === target),
            true,
        );

    const getTargetCount = (target) => getTargetCards(target).length;

    const isCardInDeck = (card) => !assignments[card];

    const handleLibraryCardClick = (card) => {
        if (!isCardInDeck(card)) {
            return;
        }
        setAssignments((prev) => {
            const countForActive = Object.values(prev).filter((target) => target === activeTarget).length;
            if (countForActive >= TARGET_LIMITS[activeTarget]) {
                return prev;
            }
            return { ...prev, [card]: activeTarget };
        });
    };

    const handleAssignedCardClick = (event, card) => {
        event.stopPropagation();
        setAssignments((prev) => {
            const next = { ...prev };
            delete next[card];
            return next;
        });
    };

    const handleClearAll = () => {
        setAssignments({});
    };

    const handleConfirm = () => {
        const hands = [getTargetCards('0'), getTargetCards('1'), getTargetCards('2')];
        const landlordCards = getTargetCards('landlord');

        if (hands[0].length !== 17 || hands[1].length !== 17 || hands[2].length !== 17 || landlordCards.length !== 3) {
            return;
        }

        onConfirm({ hands, landlordCards });
    };

    const isValid =
        getTargetCount('0') === 17 &&
        getTargetCount('1') === 17 &&
        getTargetCount('2') === 17 &&
        getTargetCount('landlord') === 3;

    const renderCardFace = (card) => {
        const [rankClass, suitClass, rankText, suitText] = translateCardData(card);
        return (
            <span className={`card ${rankClass} ${suitClass}`}>
                <span className="rank">{rankText}</span>
                <span className="suit">{suitText}</span>
            </span>
        );
    };

    const getTargetLabel = (target) => {
        if (target === 'landlord') {
            return t('doudizhu.assign_landlord_cards');
        }
        return t('doudizhu.assign_to_id', { id: target });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="manual-deal-dialog">
            <DialogTitle>{t('doudizhu.manual_deal_title')}</DialogTitle>
            <DialogContent>
                <p className="manual-deal-hint">{t('doudizhu.manual_deal_hint')}</p>
                <div className="manual-deal-layout">
                    <div className="manual-deal-assignments">
                        {TARGETS.map((target) => {
                            const targetCards = getTargetCards(target);
                            return (
                                <div
                                    key={`manual-deal-row-${target}`}
                                    className={`manual-deal-row manual-deal-row--${target}${
                                        activeTarget === target ? ' manual-deal-row--active' : ''
                                    }`}
                                    onClick={() => setActiveTarget(target)}
                                >
                                    <div className="manual-deal-row-header">
                                        <span className="manual-deal-row-title">{getTargetLabel(target)}</span>
                                        <span className="manual-deal-row-count">
                                            {getTargetCount(target)}/{TARGET_LIMITS[target]}
                                        </span>
                                    </div>
                                    <div className="manual-deal-row-cards playingCards loose">
                                        {targetCards.length > 0 ? (
                                            <ul
                                                className="hand"
                                                style={{ width: computeHandCardsWidth(targetCards.length, 10) }}
                                            >
                                                {targetCards.map((card) => (
                                                    <li key={`assigned-${target}-${card}`}>
                                                        <button
                                                            type="button"
                                                            className="manual-deal-assigned-card"
                                                            onClick={(event) => handleAssignedCardClick(event, card)}
                                                        >
                                                            {renderCardFace(card)}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span className="manual-deal-row-empty">{t('doudizhu.manual_deal_empty')}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <Button size="small" onClick={handleClearAll} className="manual-deal-clear">
                            {t('doudizhu.clear_deal')}
                        </Button>
                    </div>
                    <div className="manual-deal-deck-panel">
                        <div className="manual-deal-deck-title">{t('doudizhu.card_library')}</div>
                        <div className="manual-deal-deck playingCards">
                            {DECK_ROWS.map((row, rowIndex) => (
                                <div key={`deck-row-${rowIndex}`} className="manual-deal-deck-row">
                                    {row.map((card) => {
                                        if (!isCardInDeck(card)) {
                                            return (
                                                <div
                                                    key={`deck-slot-empty-${card}`}
                                                    className="manual-deal-deck-slot manual-deal-deck-slot--empty"
                                                />
                                            );
                                        }
                                        return (
                                            <button
                                                key={`deck-card-${card}`}
                                                type="button"
                                                className="manual-deal-deck-slot manual-deal-deck-card"
                                                onClick={() => handleLibraryCardClick(card)}
                                            >
                                                {renderCardFace(card)}
                                            </button>
                                        );
                                    })}
                                    {row.length < 4 &&
                                        Array.from({ length: 4 - row.length }).map((_, idx) => (
                                            <div
                                                key={`deck-row-${rowIndex}-pad-${idx}`}
                                                className="manual-deal-deck-slot manual-deal-deck-slot--empty"
                                            />
                                        ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('cancel')}</Button>
                <Button color="primary" variant="contained" disabled={!isValid} onClick={handleConfirm}>
                    {t('doudizhu.confirm_deal')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ManualDealDialog;
